import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Camera, LayoutDashboard, Calendar, Users, UserCog, Briefcase, LogOut, MapPin, ImageIcon, FileText, ChevronLeft, ChevronRight, Menu, X, CreditCard, User as UserIcon, ChevronDown, Image as ImageGalleryIcon, Building2, FileSignature, Receipt, Clock, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import CreditBalancePill from '@/components/CreditBalancePill';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export default function Sidebar({ user: userProp }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user: ctxUser, logout } = useAuth();
  const user = ctxUser || userProp;
  
  // Desktop collapsed state
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });

  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Save desktop state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', isCollapsed.toString());
    
    if (isCollapsed) {
      document.body.classList.add('sidebar-collapsed');
    } else {
      document.body.classList.remove('sidebar-collapsed');
    }
    
    window.dispatchEvent(new Event('sidebarToggle'));
  }, [isCollapsed]);
  
  useEffect(() => {
    if (isCollapsed) {
      document.body.classList.add('sidebar-collapsed');
    }
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const teamRole = user?.team_role || 'owner';
  const isPhotoEd = teamRole === 'photo_editor';

  // CRM (Clients/Quotes/Proposals/Invoices/Timesheets/Reports) is hidden until
  // the feature is functional. Flip to true to bring the section back.
  const CRM_ENABLED = false;

  // Photo editors see a stripped-down sidebar — Events (drill into event's
  // Photos tab) + the global Photos gallery.
  const links = isPhotoEd
    ? [
        { to: '/events', icon: Calendar, label: t('sidebar.events', { defaultValue: 'Events' }) },
        { to: '/photos', icon: ImageGalleryIcon, label: t('sidebar.photos', { defaultValue: 'Photos' }) },
      ]
    : [
        { to: '/dashboard', icon: LayoutDashboard, label: t('sidebar.dashboard', { defaultValue: 'Dashboard' }) },
        { to: '/events', icon: Calendar, label: t('sidebar.events', { defaultValue: 'Events' }) },
        { to: '/photos', icon: ImageGalleryIcon, label: t('sidebar.photos', { defaultValue: 'Photos' }) },
        { to: '/photographers', icon: Users, label: t('sidebar.photographers', { defaultValue: 'Photographers' }) },
        { to: '/image-library', icon: ImageIcon, label: t('sidebar.imageLibrary', { defaultValue: 'Image Library' }) },
        { to: '/content-templates', icon: FileText, label: t('sidebar.contentTemplates', { defaultValue: 'Content Templates' }) },
        { to: '/gear', icon: Briefcase, label: t('sidebar.gear', { defaultValue: 'Gear & Kits' }) },
      ];

  // CRM section — hidden behind CRM_ENABLED until it works.
  if (!isPhotoEd && CRM_ENABLED) {
    links.push(
      { divider: 'CRM' },
      { to: '/clients', icon: Building2, label: t('sidebar.clients', { defaultValue: 'Clients' }) },
      { to: '/quotes', icon: FileSignature, label: t('sidebar.quotes', { defaultValue: 'Quotes' }) },
      { to: '/proposals', icon: FileText, label: t('sidebar.proposals', { defaultValue: 'Proposals' }) },
      { to: '/invoices', icon: Receipt, label: t('sidebar.invoices', { defaultValue: 'Invoices' }) },
      { to: '/timesheets', icon: Clock, label: t('sidebar.timesheets', { defaultValue: 'Timesheets' }) },
      { to: '/reports', icon: TrendingUp, label: t('sidebar.reports', { defaultValue: 'Reports' }) },
    );
  }

  // Team is visible only to coordinators (not photographers).
  const canSeeTeam = user?.role !== 'photographer' && ['owner', 'admin'].includes(teamRole);
  if (canSeeTeam) {
    links.push({ to: '/team', icon: UserCog, label: t('sidebar.team', { defaultValue: 'Team' }) });
  }

  if (user?.role === 'photographer') {
    links.push({ to: '/photographer/roster', icon: MapPin, label: t('sidebar.myRoster', { defaultValue: 'My Roster' }) });
  }
  // Admin Panel intentionally NOT shown here — admins access via /backend-login.

  return (
    <>
      {/* Mobile Hamburger Button - Only visible on mobile */}
      <button
        onClick={toggleMobileMenu}
        className="fixed top-4 left-4 z-50 md:hidden bg-slate-800 border-2 border-slate-600 rounded-lg p-2.5 shadow-2xl hover:bg-slate-700 transition-all text-white"
        aria-label="Toggle menu"
      >
        {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Desktop Toggle Button - Hidden on mobile */}
      <button
        onClick={toggleSidebar}
        className={`
          hidden md:block fixed top-4 z-[60]
          ${isCollapsed ? 'left-4' : 'left-[272px]'}
          bg-slate-800 border-2 border-slate-600 rounded-lg p-2.5 
          shadow-2xl hover:bg-slate-700 transition-all text-white hover:scale-105
        `}
        style={{ transition: 'left 0.3s ease-in-out' }}
        title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {isCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
      </button>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={toggleMobileMenu}
        />
      )}

      {/* Sidebar */}
      <div 
        className={`
          fixed inset-y-0 left-0 z-50
          bg-gradient-to-b from-slate-900 to-slate-800
          transition-all duration-300 ease-in-out
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0
          ${isCollapsed ? 'md:-translate-x-full md:w-0 md:min-w-0' : 'w-64 md:w-64 md:min-w-[256px]'}
          h-screen overflow-y-auto
          flex flex-col p-4 md:p-6
          shadow-2xl
        `}
        style={{
          pointerEvents: isCollapsed ? 'none' : 'auto'
        }}
      >
        {!isCollapsed && (
          <>
            <div className="mb-8 mt-16">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
                  <Camera className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-white font-bold text-lg">PhotoPlan</h2>
                  <p className="text-slate-400 text-xs">by theking.studio</p>
                </div>
              </div>
            </div>

            <nav className="space-y-2 mb-8">
              {links.map((link, idx) => {
                if (link.divider) {
                  return (
                    <p key={`divider-${idx}`} className="text-[10px] uppercase tracking-wide text-slate-500 mt-4 mb-1 px-3">
                      {link.divider}
                    </p>
                  );
                }
                const Icon = link.icon;
                const isActive = location.pathname === link.to;
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`sidebar-link ${isActive ? 'active' : ''}`}
                    data-testid={`sidebar-link-${link.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm md:text-base">{link.label}</span>
                  </Link>
                );
              })}
            </nav>

            <div className="mt-auto pt-6 border-t border-slate-700">
              <div className="mb-2">
                <LanguageSwitcher variant="outline" />
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger className="w-full text-left p-3 rounded-lg bg-slate-800/50 hover:bg-slate-800 transition flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm truncate">{user?.name || user?.email}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-slate-400 text-xs capitalize">{user?.role}</p>
                      <CreditBalancePill />
                    </div>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                </DropdownMenuTrigger>
                <DropdownMenuContent side="top" align="start" className="w-56">
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <UserIcon className="w-4 h-4 mr-2" /> {t('profile.menuProfile')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/billing')}>
                    <CreditCard className="w-4 h-4 mr-2" /> {t('profile.menuBilling')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-700">
                    <LogOut className="w-4 h-4 mr-2" /> {t('profile.menuSignOut')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </>
        )}
      </div>
    </>
  );
}
